import { isDefined } from "@clipboard-health/util-ts";
import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { type TuFunction } from "@src/appV2/i18n/types";

import { type ChannelListItem, type ChannelMetadata } from "./types";

const fallbackTu: TuFunction = (defaultText) => defaultText;

/**
 * Parse a Sendbird channel URL to extract IDs.
 * Supports both flex channels (workplaceId_workerId) and
 * placement channels (workplaceId_workerId_placementId).
 */
export function parseChannelUrlForList(channelUrl: string):
  | {
      workplaceId: string;
      workerId: string;
      placementId?: string;
    }
  | undefined {
  const parts = channelUrl.split("_");
  if (parts.length === 2) {
    return { workplaceId: parts[0], workerId: parts[1] };
  }
  if (parts.length === 3) {
    return { workplaceId: parts[0], workerId: parts[1], placementId: parts[2] };
  }
  return undefined;
}

/**
 * Extract metadata from a Sendbird GroupChannel's cachedMetaData.
 */
export function extractChannelMetadata(channel: GroupChannel): ChannelMetadata {
  const metaData = channel.cachedMetaData as Record<string, string> | undefined;
  if (!isDefined(metaData)) {
    return {};
  }

  return {
    hcpName: metaData.hcpName || undefined,
    facilityName: metaData.facilityName || undefined,
    lastBooked: metaData.lastBooked || undefined,
    agentReq: metaData.agentReq || undefined,
    placementTitle: metaData.placementTitle || undefined,
    placementCandidateAccessId: metaData.placementCandidateAccessId || undefined,
    managingFacilityId: metaData.managingFacilityId || undefined,
  };
}

/**
 * Resolve the display name for the worker in a channel.
 * Uses metadata hcpName, then falls back to channel name parsing,
 * then to the member whose userId matches the workerId parsed from the channel URL.
 */
export function resolveWorkerName(
  channel: GroupChannel,
  metadata: ChannelMetadata,
  workerId?: string,
  tu: TuFunction = fallbackTu
): string {
  if (isDefined(metadata.hcpName) && metadata.hcpName.length > 0) {
    return metadata.hcpName;
  }

  // Legacy fallback: channel name is "FacilityName - WorkerName"
  const nameParts = channel.name.split("-");
  if (nameParts.length >= 2) {
    const workerName = nameParts[1].trim();
    if (workerName.length > 0) {
      return workerName;
    }
  }

  // Last resort: find the worker member by their userId
  if (isDefined(workerId)) {
    const workerMember = channel.members.find((member) => member.userId === workerId);
    if (isDefined(workerMember) && workerMember.nickname.length > 0) {
      return workerMember.nickname;
    }
  }

  return tu("Unknown");
}

const CLIPBOARD_GUIDE_CHANNEL_SUFFIX = "_clipboard-guide";

/**
 * Check if a channel URL belongs to the Clipboard Guide system channel.
 * These channels are internal and should be hidden from the chat list.
 */
export function isClipboardGuideChannel(channelUrl: string): boolean {
  return channelUrl.endsWith(CLIPBOARD_GUIDE_CHANNEL_SUFFIX);
}

/**
 * Convert a Sendbird GroupChannel to a ChannelListItem.
 * Returns undefined if the channel URL cannot be parsed or is a Clipboard Guide channel.
 */
export function toChannelListItem(
  channel: GroupChannel,
  tu: TuFunction = fallbackTu
): ChannelListItem | undefined {
  if (isClipboardGuideChannel(channel.url)) {
    return undefined;
  }

  const parsed = parseChannelUrlForList(channel.url);
  if (!isDefined(parsed)) {
    return undefined;
  }

  const metadata = extractChannelMetadata(channel);
  const workerName = resolveWorkerName(channel, metadata, parsed.workerId, tu);

  return {
    channel,
    metadata,
    workplaceId: parsed.workplaceId,
    workerId: parsed.workerId,
    placementId: parsed.placementId,
    workerName,
  };
}

/**
 * Filter a list of channel items by starred worker IDs (client-side).
 */
export function filterByStarredWorkerIds(
  items: readonly ChannelListItem[],
  starredWorkerIds: readonly string[]
): readonly ChannelListItem[] {
  const starredSet = new Set(starredWorkerIds);
  return items.filter((item) => starredSet.has(item.workerId));
}
